import 'leaflet/dist/leaflet.css';
import { BASEMAP_TILE_URL, BASEMAP_ATTRIBUTION } from '../../basemap.js';
import './map.css';
import './bolt-on.css';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useCatchmentLink, useCatchmentKey, useCatchmentOwner } from '../catchment-link.js';
import { ChevronDown, Star, Check, Plus, BarChart3 } from 'lucide-react';
import {
  AttributionControl,
  Circle,
  CircleMarker,
  MapContainer,
  Polygon,
  Popup,
  TileLayer,
  useMap,
  useMapEvents,
} from 'react-leaflet';
import * as Slider from '@radix-ui/react-slider';
import {
  CATCHMENT_BANDS_BY_MODE,
  DEFAULT_CATCHMENT_BAND,
  DEFAULT_TRAVEL_MODE,
  type CatchmentBand,
  type TravelMode,
} from '@hc/shared';
import { PanelFrame } from '../panel-frame.js';
import { REVENUE_STOPS, nearestStopIndex } from '../revenue-scale.js';
import {
  MAP_REVENUE_MIN,
  MAP_REVENUE_MAX,
  formatMapRevenueBound,
  type RevenueRange,
} from '../map-filters.js';
import { useBoltOn, useBoltOnFootprint, useBoltOnMulti } from '../../api/bolt-on-hooks.js';
import { useFacilitiesFacets } from '../../api/facilities-hooks.js';
import { useWatchlists, useCreateWatchlist, useAddEntitiesToWatchlist } from '../../api/hooks.js';
import type { BoltOnPoint, BoltOnQuery, BoltOnMultiQuery } from '../../api/client.js';
import { useRollupLink } from '../rollup-link.js';
import { MapPopupActions } from '../map-popup-actions.js';
import { useOpenChannelPanel } from '../use-open-channel-panel.js';
import { Button } from '../button.js';
import { useGlobalCurrency, usePanelEntityId, useSetPanelEntity } from '../store.js';
import { convertFromEur, CURRENCY_SYMBOL, type GlobalCurrency } from '../../currency.js';
import type { PanelId, TabId } from '../store.js';

// Bolt-On Screening panel (handover: Bolt_On_Screening_Panel). Drop a seed pin,
// pick a subsegment + drive-time band → a catchment of reachable independent
// targets (the actionable bolt-ons) plus the competitor-owned sites already
// rolling up there. Phase 1 = radius catchment (honest, labelled "approx");
// drive-time isochrones are Phase 2. Reuses the screener surface + the
// economic-group / sponsor resolution for independence + the competitor layer.

const UK_CENTER: [number, number] = [54.4, -3.2];
const UK_ZOOM = 6;

// A closed circle ring (GeoJSON [lon,lat] order) approximating a radius-mode
// catchment, so the Census panel always receives a polygon to profile even when
// there's no routed isochrone (radius fallback / public transport).
// Stable refs for the Census-button open call (both are hook deps).
const CENSUS_PANEL_SIZE = { width: 460, height: 560 };
const CENSUS_FOLLOW_ON_OPEN = { followLinkOnOpen: true };

function circleRing(lat: number, lng: number, radiusKm: number, n = 64): number[][] {
  const dLat = radiusKm / 111.32;
  const dLng = radiusKm / (111.32 * Math.max(Math.cos((lat * Math.PI) / 180), 1e-6));
  const ring: number[][] = [];
  for (let i = 0; i <= n; i++) {
    const t = (2 * Math.PI * i) / n;
    ring.push([lng + dLng * Math.cos(t), lat + dLat * Math.sin(t)]);
  }
  return ring;
}

function readToken(name: string, fallback: string): string {
  if (typeof window === 'undefined') return fallback;
  const v = getComputedStyle(document.documentElement).getPropertyValue(name).trim();
  return v || fallback;
}

/** Keep Leaflet's size synced to the dockview panel (mirrors map.tsx). */
function InvalidateOnResize() {
  const map = useMap();
  useEffect(() => {
    const container = map.getContainer();
    const ro = new ResizeObserver(() => map.invalidateSize());
    ro.observe(container);
    const t = setTimeout(() => map.invalidateSize(), 60);
    return () => {
      ro.disconnect();
      clearTimeout(t);
    };
  }, [map]);
  return null;
}

interface Seed {
  lat: number;
  lng: number;
}

/** Click anywhere to (re)drop the seed pin. */
function SeedDropper({ onDrop }: { onDrop: (s: Seed) => void }) {
  useMapEvents({ click: (e) => onDrop({ lat: e.latlng.lat, lng: e.latlng.lng }) });
  return null;
}

/** Fly the map to a programmatically-set seed (e.g. a company footprint). Fires
 *  once per coordinate — a user-dropped pin doesn't recenter. */
function FlyTo({ lat, lng }: { lat: number; lng: number }) {
  const map = useMap();
  useEffect(() => {
    map.flyTo([lat, lng], Math.max(map.getZoom(), 10), { animate: true });
  }, [map, lat, lng]);
  return null;
}

/** Fit the map to a set of points — used by the Roll-Up coverage view to frame
 *  all foundation members. Re-fits when the point set changes (keyed string). */
function FitBounds({ points }: { points: Array<{ lat: number; lng: number }> }) {
  const map = useMap();
  const key = points.map((p) => `${p.lat.toFixed(3)},${p.lng.toFixed(3)}`).join('|');
  useEffect(() => {
    if (points.length === 0) return;
    if (points.length === 1) {
      map.flyTo([points[0]!.lat, points[0]!.lng], Math.max(map.getZoom(), 10), { animate: true });
      return;
    }
    const lats = points.map((p) => p.lat);
    const lngs = points.map((p) => p.lng);
    map.fitBounds(
      [
        [Math.min(...lats), Math.min(...lngs)],
        [Math.max(...lats), Math.max(...lngs)],
      ],
      { padding: [40, 40], maxZoom: 12, animate: true },
    );
  }, [map, key]);
  return null;
}

function fmtMoney(eur: number | null, ccy: GlobalCurrency): string {
  if (eur == null) return '—';
  const v = convertFromEur(eur, ccy);
  const a = Math.abs(v);
  const sym = CURRENCY_SYMBOL[ccy];
  if (a >= 1e9) return sym + (v / 1e9).toFixed(1) + 'B';
  if (a >= 1e6) return sym + (v / 1e6).toFixed(2) + 'M';
  if (a >= 1e3) return sym + (v / 1e3).toFixed(0) + 'K';
  return sym + Math.round(v);
}

// Compact revenue-estimate range slider — the same non-linear REVENUE_STOPS
// ladder + EUR bound formatting the Screener and Map use, so the three panels'
// revenue filters read identically.
function BoltRevenueSlider({
  value,
  currency,
  onChange,
}: {
  value: RevenueRange;
  currency: GlobalCurrency;
  onChange: (next: RevenueRange) => void;
}) {
  const lo = nearestStopIndex(value[0]);
  const hi = nearestStopIndex(value[1]);
  return (
    <div className="bolt-rev-wrap">
      <Slider.Root
        className="bolt-rev-root"
        value={[lo, hi]}
        min={0}
        max={REVENUE_STOPS.length - 1}
        step={1}
        onValueChange={(vs) => {
          if (vs.length === 2)
            onChange([REVENUE_STOPS[vs[0]!]!, REVENUE_STOPS[vs[1]!]!] as RevenueRange);
        }}
      >
        <Slider.Track className="bolt-rev-track">
          <Slider.Range className="bolt-rev-range" />
        </Slider.Track>
        <Slider.Thumb className="bolt-rev-thumb" aria-label="Minimum revenue" />
        <Slider.Thumb className="bolt-rev-thumb" aria-label="Maximum revenue" />
      </Slider.Root>
      <div className="bolt-rev-bounds" data-numeric>
        <span>{formatMapRevenueBound(REVENUE_STOPS[lo]!, 'min', currency)}</span>
        <span>{formatMapRevenueBound(REVENUE_STOPS[hi]!, 'max', currency)}</span>
      </div>
    </div>
  );
}

// Revenue control collapsed to a trigger button + popover — the slider is
// hidden until the button is clicked (outside-click / Esc closes it). The
// trigger shows the current band ("Any" when at full range).
function RevenueDropdown({
  value,
  currency,
  onChange,
}: {
  value: RevenueRange;
  currency: GlobalCurrency;
  onChange: (next: RevenueRange) => void;
}) {
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);
  useEffect(() => {
    if (!open) return undefined;
    function onDown(e: MouseEvent) {
      if (!ref.current?.contains(e.target as Node)) setOpen(false);
    }
    function onEsc(e: KeyboardEvent) {
      if (e.key === 'Escape') setOpen(false);
    }
    document.addEventListener('mousedown', onDown);
    document.addEventListener('keydown', onEsc);
    return () => {
      document.removeEventListener('mousedown', onDown);
      document.removeEventListener('keydown', onEsc);
    };
  }, [open]);
  const active = value[0] !== MAP_REVENUE_MIN || value[1] !== MAP_REVENUE_MAX;
  const summary = active
    ? `${formatMapRevenueBound(value[0], 'min', currency)} – ${formatMapRevenueBound(value[1], 'max', currency)}`
    : 'Any';
  return (
    <div ref={ref} className="bolt-ctl bolt-rev-dd">
      <span>Revenue</span>
      <button
        type="button"
        className="bolt-rev-trigger"
        data-active={active || undefined}
        onClick={() => setOpen((v) => !v)}
      >
        <span>{summary}</span>
        <ChevronDown size={11} />
      </button>
      {open && (
        <div className="bolt-rev-pop">
          <BoltRevenueSlider value={value} currency={currency} onChange={onChange} />
        </div>
      )}
    </div>
  );
}

// Bulk "Add all candidates to watchlist" — exports the whole candidate set onto
// a new or existing watchlist in one shot (the panel's headline export action).
function AddAllToWatchlist({ entityIds }: { entityIds: string[] }) {
  const [open, setOpen] = useState(false);
  const [naming, setNaming] = useState(false);
  const [draft, setDraft] = useState('');
  const [doneId, setDoneId] = useState<string | null>(null);
  const ref = useRef<HTMLDivElement>(null);
  const { data: watchlistsData } = useWatchlists();
  const createMut = useCreateWatchlist();
  const bulkMut = useAddEntitiesToWatchlist();
  const watchlists = watchlistsData?.data ?? [];
  const n = entityIds.length;

  useEffect(() => {
    if (!open) return undefined;
    function onDown(e: MouseEvent) {
      if (!ref.current?.contains(e.target as Node)) {
        setOpen(false);
        setNaming(false);
      }
    }
    function onEsc(e: KeyboardEvent) {
      if (e.key === 'Escape') {
        setOpen(false);
        setNaming(false);
      }
    }
    document.addEventListener('mousedown', onDown);
    document.addEventListener('keydown', onEsc);
    return () => {
      document.removeEventListener('mousedown', onDown);
      document.removeEventListener('keydown', onEsc);
    };
  }, [open]);

  const flashDone = (id: string) => {
    setDoneId(id);
    window.setTimeout(() => {
      setDoneId(null);
      setOpen(false);
    }, 900);
  };
  const addAllTo = (watchlistId: string) => {
    if (!n) return;
    bulkMut.mutate({ watchlistId, entityIds }, { onSuccess: () => flashDone(watchlistId) });
  };
  const createAndAdd = () => {
    const name = draft.trim();
    if (!name) return;
    createMut.mutate(name, {
      onSuccess: ({ data }) => {
        bulkMut.mutate({ watchlistId: data.id, entityIds });
        setNaming(false);
        setDraft('');
        flashDone(data.id);
      },
    });
  };

  return (
    <div ref={ref} className="bolt-addall">
      <button
        type="button"
        className="bolt-addall-btn"
        disabled={!n}
        onClick={() => setOpen((v) => !v)}
        title="Add every candidate to a ward"
      >
        <Star size={11} />
        Add all to ward{n ? ` (${n})` : ''}
        <ChevronDown size={10} />
      </button>
      {open && (
        <div className="bolt-addall-menu" role="menu">
          {watchlists.length === 0 && <div className="bolt-addall-empty">No wards yet.</div>}
          {watchlists.map((w) => (
            <button
              key={w.id}
              type="button"
              role="menuitem"
              className="bolt-addall-item"
              onClick={() => addAllTo(w.id)}
            >
              <span className="bolt-addall-name">{w.name}</span>
              {doneId === w.id ? (
                <Check size={12} color="var(--accent)" />
              ) : (
                <span className="bolt-addall-count">{w.entityCount.toLocaleString('en-GB')}</span>
              )}
            </button>
          ))}
          <div className="bolt-addall-sep">
            {naming ? (
              <div className="bolt-addall-new">
                <input
                  autoFocus
                  value={draft}
                  onChange={(e) => setDraft(e.target.value)}
                  placeholder="New ward…"
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') {
                      e.preventDefault();
                      createAndAdd();
                    } else if (e.key === 'Escape') {
                      e.preventDefault();
                      setNaming(false);
                    }
                  }}
                />
                <button
                  type="button"
                  onClick={createAndAdd}
                  disabled={!draft.trim() || createMut.isPending}
                >
                  Create
                </button>
              </div>
            ) : (
              <button type="button" className="bolt-addall-item" onClick={() => setNaming(true)}>
                <Plus size={12} />
                <span>Create new ward…</span>
              </button>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

export function BoltOnPanel({
  tabId,
  panelId,
  initialState,
}: {
  tabId: TabId;
  panelId: PanelId;
  initialState?: Record<string, unknown>;
}) {
  const accent = useMemo(() => readToken('--accent', '#a8572a'), []);
  // Corryk pin palette (brand tokens, not an ad-hoc rainbow): the independent
  // TARGET — the actionable bolt-on — is the terracotta brand accent; a
  // COMPETITOR-owned site is claret ("taken"); anything uncategorised is the
  // neutral data grey. A legend on the map spells these out.
  const pinPalette = useMemo(
    () => ({
      target: accent,
      competitor: readToken('--claret', '#990f3d'),
      other: readToken('--neutral-data', '#6e7681'),
      anchor: readToken('--text-primary', '#1a1410'),
    }),
    [accent],
  );
  const ccy = useGlobalCurrency();

  // Channel link: the catchment follows the channel's selected company, and a
  // pin click broadcasts back to the channel (so a linked Company Overview etc.
  // follows). useSetPanelEntity writes to this panel's link group.
  const channelEntityId = usePanelEntityId(tabId, panelId);
  const setChannelEntity = useSetPanelEntity(tabId, panelId);
  const selfSelectRef = useRef<string | null>(null);
  const selectEntity = useCallback(
    (id: string) => {
      selfSelectRef.current = id; // suppress the inbound re-seed our own broadcast triggers
      setChannelEntity(id);
    },
    [setChannelEntity],
  );

  // Coverage mode: instead of a single seed + catchment, profile EACH of N
  // points' catchments stacked. Two sources — a linked Roll-Up Foundation (its
  // members, each in its own subsegment), OR a multi-site operator seeded from a
  // Record (all its sites — so a dispersed chain shows every site, not one
  // misleading centroid). `coverage` is finalised below once the footprint loads.
  const catchKey = useCatchmentKey(tabId, panelId);
  const activeRollup = useRollupLink((s) => s.byKey[catchKey] ?? null);
  const rollupCoverage = !!activeRollup && activeRollup.points.length > 0;
  // This panel broadcasts its catchment over the channel; Census reads the same
  // channel. The header button opens (or focuses) a Census on this channel —
  // followLinkOnOpen tells that Census to show THIS catchment immediately (a
  // Census opened cold from the palette instead starts blank).
  const openCensus = useOpenChannelPanel(
    tabId,
    panelId,
    'census',
    'Census',
    CENSUS_PANEL_SIZE,
    CENSUS_FOLLOW_ON_OPEN,
  );

  const [seed, setSeed] = useState<Seed | null>(null);
  const [subsegment, setSubsegment] = useState<string>('');
  const [band, setBand] = useState<CatchmentBand>(DEFAULT_CATCHMENT_BAND);
  const [travel, setTravel] = useState<TravelMode>(DEFAULT_TRAVEL_MODE);
  const [revenue, setRevenue] = useState<RevenueRange>([MAP_REVENUE_MIN, MAP_REVENUE_MAX]);
  // Coverage travel-time: "Auto" = each member uses its own subsegment×urbanity
  // catchment; turning it off applies band/travel uniformly to every member.
  const [coverageAuto, setCoverageAuto] = useState(true);

  // Subsegment options = the canonical-17 the facets endpoint serves (same list
  // the Screener + Map use), so the bolt-on filter matches the rest of the app.
  // Subsegment options must populate as soon as the panel mounts — not deferred
  // behind panel-visibility (the gate raced the prod build and left the
  // dropdown empty, so no catchment could be run). The facets query is tiny and
  // shared/cached, so always fetch it.
  const { data: facets } = useFacilitiesFacets();
  const subOptions = facets?.subsegment ?? [];

  // Seed-from-company: a deliberately-launched catchment (the Record "Catchment"
  // / "Find bolt-ons" button passes initialState.seedEntityId) drives the seed;
  // when the seed entity changes we fetch its footprint and re-drop the pin. A
  // selection THIS panel broadcast is skipped so clicking a target doesn't drag
  // the whole catchment onto it.
  const initialSeedEntityId =
    typeof initialState?.['seedEntityId'] === 'string'
      ? (initialState['seedEntityId'] as string)
      : null;
  // The entity to seed from. A panel opened cold (command palette / search bar)
  // must NOT lock onto whatever entity happens to be selected on the channel at
  // mount — it opens blank, ready for the user to drop a pin (or to follow the
  // NEXT channel selection). Only an explicit initialState seed lands a company
  // on mount. Once the channel selection CHANGES from its mount value, the
  // channel drives (a linked Record / Triage selection still moves the catchment).
  const mountChannelEntityRef = useRef(channelEntityId);
  const seedEntityId =
    channelEntityId !== mountChannelEntityRef.current
      ? channelEntityId
      : (initialSeedEntityId ?? null);
  const { data: footprint } = useBoltOnFootprint(seedEntityId);
  // A multi-site seeded operator → coverage of ALL its sites (the centroid of a
  // dispersed chain is meaningless). Single-site / office-only stays single-pin.
  const entitySites = footprint?.sites ?? [];
  const entityCoverage = !rollupCoverage && entitySites.length > 1;
  const coverage = rollupCoverage || entityCoverage;
  const appliedSeedRef = useRef<string | null>(null);
  useEffect(() => {
    if (!footprint || !seedEntityId) return;
    if (appliedSeedRef.current === seedEntityId) return;
    appliedSeedRef.current = seedEntityId;
    if (selfSelectRef.current === seedEntityId) {
      selfSelectRef.current = null;
      return;
    }
    if (footprint.lat !== null && footprint.lng !== null) {
      setSeed({ lat: footprint.lat, lng: footprint.lng });
    }
    if (footprint.subsegment) setSubsegment(footprint.subsegment);
  }, [footprint, seedEntityId]);

  // Default the subsegment to the first option once facets load — unless a
  // company footprint is supplying one.
  useEffect(() => {
    if (!subsegment && !seedEntityId && subOptions.length) setSubsegment(subOptions[0]!);
  }, [subsegment, seedEntityId, subOptions]);

  const query = useMemo<BoltOnQuery | null>(() => {
    // Request a drive-time isochrone; the server returns it when the routing
    // key is set + ORS responds, else falls back to radius (boundary.mode says
    // which, and the header label reflects it).
    if (!seed || !subsegment) return null;
    const q: BoltOnQuery = { ...seed, subsegment, band, travel, mode: 'isochrone' };
    // Only send a bound when the user has moved it off the floor/cap.
    if (revenue[0] !== MAP_REVENUE_MIN) q.turnoverEurMin = revenue[0];
    if (revenue[1] !== MAP_REVENUE_MAX) q.turnoverEurMax = revenue[1];
    return q;
  }, [seed, subsegment, band, travel, revenue]);
  // The catchment query already self-gates on query !== null (seed +
  // subsegment chosen). In coverage mode the single-pin query is suppressed —
  // the multi query below drives the map instead.
  const { data, isLoading, isError } = useBoltOn(coverage ? null : query);

  const points = data?.points ?? [];
  const candidates = data?.candidates ?? [];
  const counts = data?.counts;
  const boundary = data?.boundary;

  // The coverage points + their pins, from whichever source is active:
  //   • Roll-Up Foundation → its members (each in its OWN subsegment);
  //   • multi-site operator → its sites (all in the operator's subsegment).
  const coveragePins = useMemo(
    () =>
      rollupCoverage
        ? (activeRollup?.points ?? []).map((p) => ({
            lat: p.lat,
            lng: p.lng,
            name: p.name,
            synthetic: p.synthetic,
          }))
        : entitySites.map((s) => ({ lat: s.lat, lng: s.lng, name: s.name, synthetic: false })),
    [rollupCoverage, activeRollup, entitySites],
  );
  const coverageLabel = rollupCoverage
    ? (activeRollup?.label ?? 'Roll-Up Foundation')
    : `${subsegment || 'Operator'} · ${entitySites.length} sites`;
  // One catchment per coverage point, each in its subsegment (foundation members
  // carry their own; an operator's sites all use the operator's subsegment).
  const coverageMembers = useMemo(
    () =>
      (rollupCoverage
        ? (activeRollup?.points ?? []).map((p) => ({
            lat: p.lat,
            lng: p.lng,
            subsegment: p.subsegment ?? activeRollup?.subsegment ?? '',
          }))
        : entitySites.map((s) => ({ lat: s.lat, lng: s.lng, subsegment }))
      ).filter((m) => m.subsegment),
    [rollupCoverage, activeRollup, entitySites, subsegment],
  );
  const multiQuery = useMemo<BoltOnMultiQuery | null>(() => {
    if (!coverage || coverageMembers.length === 0) return null;
    const q: BoltOnMultiQuery = { members: coverageMembers };
    // Auto → each member's own subsegment×urbanity catchment; off → uniform band.
    if (!coverageAuto) {
      q.band = band;
      q.travel = travel;
    }
    if (revenue[0] !== MAP_REVENUE_MIN) q.turnoverEurMin = revenue[0];
    if (revenue[1] !== MAP_REVENUE_MAX) q.turnoverEurMax = revenue[1];
    return q;
  }, [coverage, coverageMembers, coverageAuto, band, travel, revenue]);
  const {
    data: multiData,
    isLoading: multiLoading,
    isError: multiError,
  } = useBoltOnMulti(multiQuery);
  const coverageCounts = useMemo(() => {
    const cs = (multiData?.members ?? []).flatMap((m) => (m.catchment ? [m.catchment] : []));
    return {
      target: cs.reduce((s, c) => s + c.counts.target, 0),
      competitor: cs.reduce((s, c) => s + c.counts.competitor, 0),
    };
  }, [multiData]);

  // Broadcast the catchment polygon to its channel (Census reads the same
  // channel). Use the isochrone outline; market = the seed company's.
  const setCatchment = useCatchmentLink((s) => s.setFor);
  useEffect(() => {
    // Census profiles the POPULATION inside the polygon — independent of whether
    // any bolt-on sites fall in it. So broadcast for any catchment with a
    // boundary, not only ones with sites (the old `points.length === 0` guard
    // silently left Census on the previous area for sparse catchments).
    // In coverage mode the Roll-Up panel owns the channel's combined catchment,
    // so this single-pin broadcast stands down (don't fight over the key).
    if (coverage || !boundary) return;
    // The market, in descending order of authority:
    //   1. the seed company's own country — this catchment IS that company's;
    //   2. the market the server resolved from the seed pin (census_area
    //      nearest-area), for a hand-dropped pin with no seed company;
    //   3. the modal country of the sites found inside.
    //
    // It used to be (3) else `boundary.lng > 2 || boundary.lat < 50 ? 'FR':'GB'`
    // — a two-market leftover. Every site-less catchment in the other 25 markets
    // resolved to France or the UK: blank in most (a Bucharest polygon against
    // French IRIS areas), and near the French border SILENTLY WRONG — sampled
    // Belgian and Swiss catchments came back with up to 820k French residents
    // and French clinician density, presented as the local profile.
    //
    // Never guess. Without a market, don't broadcast: Census then keeps its
    // previous, honestly-labelled catchment instead of profiling the wrong one.
    const tally = new Map<string, number>();
    for (const p of points)
      if (p.countryCode) tally.set(p.countryCode, (tally.get(p.countryCode) ?? 0) + 1);
    const market =
      footprint?.countryCode ??
      data?.market ??
      [...tally.entries()].sort((a, b) => b[1] - a[1])[0]?.[0] ??
      null;
    if (!market) return;
    // GeoJSON [lon,lat] ring. Isochrone → the routed outline (Leaflet [lat,lng] →
    // [lon,lat]); radius/transit (no polygon) → a synthesized circle so Census
    // always has a shape to profile, never silently stops updating.
    let ring: number[][];
    if (boundary.polygon && boundary.polygon.length >= 3) {
      ring = boundary.polygon.map(([lat, lng]) => [lng, lat]);
    } else if (boundary.radiusKm > 0) {
      ring = circleRing(boundary.lat, boundary.lng, boundary.radiusKm);
    } else {
      return;
    }
    if (
      ring.length &&
      (ring[0]![0] !== ring[ring.length - 1]![0] || ring[0]![1] !== ring[ring.length - 1]![1])
    )
      ring.push(ring[0]!);
    setCatchment(catchKey, {
      polygon: { type: 'Polygon', coordinates: [ring] },
      market,
      // The seed company's region, so /census uses REGIONAL clinician density
      // where the market publishes it rather than always falling back to the
      // national figure. Hardcoded null until 2026-08-02, which meant the
      // regional supply rows we hold for 25 markets were never once read from
      // this panel. Null (a hand-dropped pin) still falls back to national.
      region: footprint?.region ?? null,
      label: `${subsegment || 'Catchment'} · ${band}-min ${travel}`,
    });
  }, [
    coverage,
    boundary,
    points,
    data,
    footprint,
    subsegment,
    band,
    travel,
    catchKey,
    setCatchment,
  ]);

  // Coverage mode: claim the channel's catchment while this panel is open, so the
  // Roll-Up (which broadcasts a fixed "Auto" catchment) yields and the travel-time
  // controls HERE drive the linked Census + Map. Released on close → Roll-Up
  // reasserts its default for the Map.
  const claimCatchment = useCatchmentOwner((s) => s.claim);
  const releaseCatchment = useCatchmentOwner((s) => s.release);
  useEffect(() => {
    if (!coverage) return;
    claimCatchment(catchKey);
    return () => releaseCatchment(catchKey);
  }, [coverage, catchKey, claimCatchment, releaseCatchment]);

  // …and broadcast the COMBINED catchment (one polygon per member) built from THIS
  // panel's multi-result, so it reflects the band/travel chosen here (Auto → each
  // member's own subsegment×urbanity catchment). Covers both sources — a Roll-Up
  // Foundation's members and a multi-site operator's sites (the latter had NO
  // broadcaster before, leaving Census blank). Keeps the prior shape until the
  // fetch settles so Census never flashes empty mid-adjustment.
  useEffect(() => {
    if (!coverage) return;
    const cats = (multiData?.members ?? []).flatMap((m) => (m.catchment ? [m.catchment] : []));
    if (cats.length === 0) return;
    const coordinates = cats.map((c) => {
      const b = c.boundary;
      const ring =
        b.polygon && b.polygon.length >= 3
          ? b.polygon.map(([lat, lng]) => [lng, lat] as number[])
          : circleRing(b.lat, b.lng, b.radiusKm);
      return [ring];
    });
    // Census is single-market: the Roll-Up's primary country, else the seeded
    // operator's own, else the market the server resolved for the first member's
    // pin. Same rule as the single-pin broadcast above — no longitude guess, and
    // no broadcast at all rather than a wrong-country profile.
    const market =
      (rollupCoverage ? activeRollup?.country : null) ??
      footprint?.countryCode ??
      cats[0]!.market ??
      null;
    if (!market) return;
    setCatchment(catchKey, {
      polygon: { type: 'MultiPolygon', coordinates },
      market,
      // National supply, deliberately: a stacked multi-site catchment spans
      // however many regions its members sit in, so no single regional
      // clinician density describes it. The single-pin path above sends one.
      region: null,
      label: coverageAuto
        ? `${coverageLabel} · auto catchments`
        : `${coverageLabel} · ${band}-min ${travel}`,
    });
  }, [
    coverage,
    multiData,
    rollupCoverage,
    activeRollup,
    footprint,
    coverageAuto,
    band,
    travel,
    coverageLabel,
    catchKey,
    setCatchment,
  ]);
  // Entity ids of the ranked candidates — the set the bulk watchlist export adds.
  const candidateEntityIds = useMemo(
    () => candidates.map((c) => c.entityId).filter((id): id is string => !!id),
    [candidates],
  );

  return (
    <PanelFrame tabId={tabId} panelId={panelId}>
      <div className="bolt-root">
        {/* controls */}
        <div className="bolt-bar">
          {coverage && (
            <span
              className="bolt-ctl"
              style={{
                flexDirection: 'row',
                alignItems: 'center',
                gap: 6,
                color: 'var(--text-secondary)',
              }}
              title={
                rollupCoverage
                  ? 'Linked to a Roll-Up Foundation — a catchment per site across every member, each in its own sub-segment (the true combined footprint).'
                  : 'Multi-site operator — a catchment per site (the centroid of a dispersed chain is misleading).'
              }
            >
              <Star size={12} color="var(--accent)" />
              <span style={{ fontWeight: 600 }}>{coverageLabel}</span>
              <span style={{ color: 'var(--text-tertiary)' }}>
                · {rollupCoverage ? 'per-site sub-segment' : 'all sites'}
              </span>
            </span>
          )}
          {/* Subsegment selector: hidden only in rollup coverage (per-member); a
              single operator (single-pin OR multi-site) has one selectable subsegment. */}
          {!rollupCoverage && (
            <label className="bolt-ctl">
              <span>Subsegment</span>
              <select value={subsegment} onChange={(e) => setSubsegment(e.target.value)}>
                {subOptions.map((s) => (
                  <option key={s} value={s}>
                    {s}
                  </option>
                ))}
              </select>
            </label>
          )}
          <label className="bolt-ctl">
            <span>Catchment</span>
            <select
              value={coverage && coverageAuto ? 'auto' : `${travel}:${band}`}
              onChange={(e) => {
                const v = e.target.value;
                if (v === 'auto') {
                  setCoverageAuto(true);
                  return;
                }
                setCoverageAuto(false);
                const [t, b] = v.split(':');
                setTravel(t as TravelMode);
                setBand(Number(b) as CatchmentBand);
              }}
            >
              {coverage && <option value="auto">Auto (per sub-segment)</option>}
              <optgroup label="Drive">
                {CATCHMENT_BANDS_BY_MODE.drive.map((b) => (
                  <option key={`drive:${b}`} value={`drive:${b}`}>
                    {b} min drive
                  </option>
                ))}
              </optgroup>
              <optgroup label="Walk">
                {CATCHMENT_BANDS_BY_MODE.walk.map((b) => (
                  <option key={`walk:${b}`} value={`walk:${b}`}>
                    {b} min walk
                  </option>
                ))}
              </optgroup>
              <optgroup label="Public transport">
                {CATCHMENT_BANDS_BY_MODE.transit.map((b) => (
                  <option key={`transit:${b}`} value={`transit:${b}`}>
                    {b} min transit
                  </option>
                ))}
              </optgroup>
            </select>
          </label>
          <RevenueDropdown value={revenue} currency={ccy} onChange={setRevenue} />
          <Button
            variant="secondary"
            onClick={openCensus}
            icon={<BarChart3 size={12} />}
            title="Open a Census panel on this channel to profile the catchment's demographics & clinician supply (focuses the existing one if already open)"
          >
            Census
          </Button>
          <div style={{ flex: 1 }} />
          <span className="bolt-counts" data-numeric>
            {coverage
              ? multiLoading
                ? 'Loading…'
                : multiError
                  ? 'Failed to load'
                  : `${coverageCounts.target} bolt-on opportunities · ${coverageCounts.competitor} competitor sites · ${coveragePins.length} sites`
              : !seed
                ? 'Click the map to drop a seed'
                : isLoading
                  ? 'Loading…'
                  : isError
                    ? 'Failed to load'
                    : counts
                      ? `${candidates.length} bolt-on opportunities · ${counts.competitor} competitor sites`
                      : ''}
            {footprint?.source === 'office' && seed && (
              <span
                style={{ color: 'var(--text-tertiary)' }}
                title="No operating sites are geocoded for this company — seeded from its registered-office postcode (approximate; a registered office can be an accountant's address)."
              >
                {' · seeded from registered office'}
              </span>
            )}
          </span>
        </div>

        <div className="bolt-body">
          {/* full-width map; candidates are exported via the overlay button */}
          <div className="bolt-map">
            {!coverage && (
              <div className="bolt-addall-wrap">
                <AddAllToWatchlist entityIds={candidateEntityIds} />
              </div>
            )}
            <MapContainer
              center={UK_CENTER}
              zoom={UK_ZOOM}
              minZoom={5}
              maxZoom={18}
              preferCanvas
              scrollWheelZoom
              attributionControl={false}
            >
              <AttributionControl prefix={false} position="bottomright" />
              <TileLayer
                className="hc-map-tiles"
                url={BASEMAP_TILE_URL}
                attribution={BASEMAP_ATTRIBUTION}
                subdomains="abcd"
                maxZoom={19}
              />
              <InvalidateOnResize />
              {!coverage && <SeedDropper onDrop={setSeed} />}
              {!coverage && footprint?.lat != null && footprint?.lng != null && (
                <FlyTo lat={footprint.lat} lng={footprint.lng} />
              )}
              {coverage && coveragePins.length > 0 && <FitBounds points={coveragePins} />}

              {/* SINGLE-PIN MODE — one seed catchment + its sites */}
              {!coverage && (
                <>
                  {/* catchment boundary — solid isochrone polygon (drive-time) or
                      dashed radius circle ("approx") */}
                  {seed && boundary?.mode === 'isochrone' && boundary.polygon && (
                    <Polygon
                      positions={boundary.polygon}
                      pathOptions={{
                        color: accent,
                        weight: 1.5,
                        opacity: 0.8,
                        fillColor: accent,
                        fillOpacity: 0.05,
                      }}
                    />
                  )}
                  {seed && boundary && boundary.mode !== 'isochrone' && (
                    <Circle
                      center={[seed.lat, seed.lng]}
                      radius={boundary.radiusKm * 1000}
                      pathOptions={{
                        color: accent,
                        weight: 1.5,
                        opacity: 0.7,
                        dashArray: '6 5',
                        fillColor: accent,
                        fillOpacity: 0.04,
                      }}
                    />
                  )}
                  {points.map((p) => (
                    <SitePin
                      key={p.id}
                      p={p}
                      palette={pinPalette}
                      ccy={ccy}
                      onSelect={selectEntity}
                    />
                  ))}
                  {seed && (
                    <CircleMarker
                      center={[seed.lat, seed.lng]}
                      radius={6}
                      pathOptions={{
                        color: pinPalette.anchor,
                        weight: 2,
                        fillColor: '#fff',
                        fillOpacity: 1,
                      }}
                    />
                  )}
                </>
              )}

              {/* ROLL-UP COVERAGE MODE — each member's catchment + sites, stacked */}
              {coverage &&
                multiData?.members.map((m, mi) => {
                  const c = m.catchment;
                  if (!c) return null; // B2B member — no residential catchment
                  return c.boundary.polygon && c.boundary.polygon.length >= 3 ? (
                    <Polygon
                      key={`bnd:${mi}`}
                      positions={c.boundary.polygon}
                      pathOptions={{
                        color: accent,
                        weight: 1.2,
                        opacity: 0.7,
                        fillColor: accent,
                        fillOpacity: 0.04,
                      }}
                    />
                  ) : (
                    <Circle
                      key={`bnd:${mi}`}
                      center={[m.lat, m.lng]}
                      radius={c.boundary.radiusKm * 1000}
                      pathOptions={{
                        color: accent,
                        weight: 1.2,
                        opacity: 0.6,
                        dashArray: '6 5',
                        fillColor: accent,
                        fillOpacity: 0.03,
                      }}
                    />
                  );
                })}
              {coverage &&
                multiData?.members.flatMap((m, mi) =>
                  (m.catchment?.points ?? []).map((p) => (
                    <SitePin
                      key={`${mi}:${p.id}`}
                      p={p}
                      palette={pinPalette}
                      ccy={ccy}
                      onSelect={selectEntity}
                    />
                  )),
                )}
              {/* the coverage points themselves — foundation members (combine
                  opcos / off-market) or the operator's own sites */}
              {coverage &&
                coveragePins.map((pt, i) => (
                  <CircleMarker
                    key={`mem:${i}`}
                    center={[pt.lat, pt.lng]}
                    radius={6}
                    pathOptions={{
                      color: pinPalette.anchor,
                      weight: 2,
                      fillColor: '#fff',
                      fillOpacity: 1,
                    }}
                  >
                    <Popup>
                      <div className="hc-map-popup-title">
                        {pt.name ??
                          (rollupCoverage
                            ? pt.synthetic
                              ? 'Off-market target'
                              : 'Member'
                            : 'Site')}
                      </div>
                      <div className="hc-map-popup-meta">
                        {rollupCoverage
                          ? `Foundation member${pt.synthetic ? ' · off-market' : ''}`
                          : 'Operating site'}
                      </div>
                    </Popup>
                  </CircleMarker>
                ))}
            </MapContainer>
            {(seed || (coverage && coveragePins.length > 0)) && (
              <div className="bolt-legend">
                <span className="bolt-legitem">
                  <span className="bolt-dot" style={{ background: pinPalette.target }} />
                  Independent target
                </span>
                <span className="bolt-legitem">
                  <span className="bolt-dot" style={{ background: pinPalette.competitor }} />
                  Competitor-owned
                </span>
                <span className="bolt-legitem">
                  <span
                    className="bolt-dot bolt-dot-ring"
                    style={{ borderColor: pinPalette.anchor }}
                  />
                  {rollupCoverage
                    ? 'Combine member'
                    : coverage
                      ? 'Operator site'
                      : 'Catchment centre'}
                </span>
              </div>
            )}
          </div>
        </div>
      </div>
    </PanelFrame>
  );
}

function SitePin({
  p,
  palette,
  ccy,
  onSelect,
}: {
  p: BoltOnPoint;
  palette: { target: string; competitor: string; other: string; anchor: string };
  ccy: GlobalCurrency;
  onSelect: (entityId: string) => void;
}) {
  const competitor = p.klass === 'competitor';
  const target = p.klass === 'target';
  const colour = competitor ? palette.competitor : target ? palette.target : palette.other;
  return (
    <CircleMarker
      center={[p.lat, p.lng]}
      radius={target ? 5 : competitor ? 4.5 : 3}
      pathOptions={{
        color: colour,
        weight: 1,
        fillColor: colour,
        fillOpacity: target ? 0.9 : competitor ? 0.85 : 0.5,
      }}
      eventHandlers={{ click: () => p.entityId && onSelect(p.entityId) }}
    >
      <Popup>
        <div className="hc-map-popup-title">{p.name ?? 'Unnamed'}</div>
        <div className="hc-map-popup-meta">
          {p.klass === 'target'
            ? 'Independent target'
            : competitor
              ? `Competitor-owned${p.group ? ` — ${p.group}` : ''}`
              : 'Other operator'}
          {' · '}
          {fmtMoney(p.turnoverEur, ccy)}
          {' · '}
          {p.distanceKm.toFixed(1)} km
        </div>
        {p.entityId ? (
          <MapPopupActions entityId={p.entityId} />
        ) : (
          <div className="hc-map-popup-meta">No linked company</div>
        )}
      </Popup>
    </CircleMarker>
  );
}
